import {
  MANUAL_STAGES,
  ORDER_STAGE_LABELS,
  STAGE_OVERRIDE_REASON_MAX,
  type Id,
  type ManualStage,
  type OrderStage,
  type StageOverrideOutcome,
  type WriteRejection,
} from "@estelle/domain";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useId, useState } from "react";

import { useDataSource } from "@/adapters/data-source";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

/** The Select's value for "follow tracking again"; no stage is called this. */
const AUTOMATIC = "automatic";

/**
 * "Change stage" (#591, #680): one dialog, for one order or the ticked ones.
 * Pick any stage, forward or back, say why in a comment, save. The comment is
 * required whichever way the stage goes, and the server adds it to the order's
 * comments, so the thread shows who changed the stage and why.
 *
 * Webhooks do not always move an order, so a person says where it is; the stage
 * holds until someone picks Automatic, which hands the order back to tracking.
 * RTO received and Cancelled are deliberately not offered: the first moves
 * stock and stays the receiving flow, the second is Shopify's.
 *
 * Local only: the server writes nothing to Shopify. Orders that cannot take the
 * change are skipped by the server and counted in what comes back.
 */
export function ChangeStageMenu({
  orderIds,
  currentStage,
  canClear = true,
  size = "sm",
  onDone,
}: {
  orderIds: readonly Id[];
  /** For one order: the stage it shows now, left out of the choices. */
  currentStage?: OrderStage;
  /** Whether Automatic does anything: false for one order that has no manual stage. */
  canClear?: boolean;
  size?: "sm" | "default";
  /** Called with the server's count of orders changed and skipped. */
  onDone: (outcome: StageOverrideOutcome, stage: ManualStage | null) => void;
}) {
  const data = useDataSource();
  const queryClient = useQueryClient();
  const stageId = useId();
  const commentId = useId();
  const [open, setOpen] = useState(false);
  const [choice, setChoice] = useState<string | null>(null);
  const [comment, setComment] = useState("");
  const [rejection, setRejection] = useState<WriteRejection | null>(null);

  const mutation = useMutation({
    mutationFn: (input: { stage: ManualStage | null; reason: string }) =>
      data.stages.setStageOverride(orderIds, input),
    onSuccess: (result, input) => {
      if (result.ok) {
        setRejection(null);
        setOpen(false);
        void queryClient.invalidateQueries({ queryKey: ["orders"] });
        onDone(result.data, input.stage);
      } else {
        setRejection(result.rejection);
      }
    },
  });

  const busy = mutation.isPending;
  const fieldError = (field: string) =>
    rejection?.kind === "invalid" ? rejection.fields?.[field] : undefined;
  const commentError = fieldError("reason");
  const stageError = fieldError("stage");
  const many = orderIds.length > 1;

  return (
    <>
      <Button
        size={size}
        variant="outline"
        disabled={busy || orderIds.length === 0}
        onClick={() => {
          setChoice(null);
          setComment("");
          setRejection(null);
          setOpen(true);
        }}
      >
        Change stage
      </Button>

      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (!busy) setOpen(next);
        }}
      >
        <DialogContent
          title={
            many
              ? `Change stage of ${String(orderIds.length)} orders`
              : "Change stage"
          }
          description="Added to the order's comments. Nothing is sent to Shopify."
        >
          <form
            className="space-y-4"
            onSubmit={(event) => {
              event.preventDefault();
              if (choice === null) return;
              mutation.mutate({
                stage: choice === AUTOMATIC ? null : (choice as ManualStage),
                reason: comment,
              });
            }}
          >
            <div className="space-y-1.5">
              <Label htmlFor={stageId} className="text-sm font-medium">
                New stage
              </Label>
              <Select
                value={choice ?? undefined}
                disabled={busy}
                onValueChange={setChoice}
              >
                <SelectTrigger
                  id={stageId}
                  className="w-full"
                  aria-invalid={stageError ? true : undefined}
                >
                  <SelectValue placeholder="Choose a stage" />
                </SelectTrigger>
                <SelectContent>
                  {MANUAL_STAGES.filter((stage) => stage !== currentStage).map(
                    (stage) => (
                      <SelectItem key={stage} value={stage}>
                        {ORDER_STAGE_LABELS[stage]}
                      </SelectItem>
                    ),
                  )}
                  {canClear ? (
                    <SelectItem value={AUTOMATIC}>
                      Automatic (follow tracking)
                    </SelectItem>
                  ) : null}
                </SelectContent>
              </Select>
              {stageError ? (
                <p className="text-destructive text-xs">{stageError}</p>
              ) : null}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor={commentId} className="text-sm font-medium">
                Comment
              </Label>
              <Input
                id={commentId}
                value={comment}
                maxLength={STAGE_OVERRIDE_REASON_MAX}
                disabled={busy}
                placeholder="Why is the stage changing?"
                aria-invalid={commentError ? true : undefined}
                onChange={(event) => {
                  setComment(event.target.value);
                }}
              />
              {commentError ? (
                <p className="text-destructive text-xs">{commentError}</p>
              ) : null}
            </div>

            {rejection && !stageError && !commentError ? (
              <p role="alert" className="text-destructive text-sm">
                {rejection.message}
              </p>
            ) : null}

            <div className="flex flex-col-reverse gap-2 border-t pt-3 sm:flex-row sm:justify-end">
              <Button
                type="button"
                variant="ghost"
                disabled={busy}
                onClick={() => {
                  setOpen(false);
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={busy || choice === null || comment.trim() === ""}
              >
                {busy ? "Saving…" : "Change stage"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
